import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { ReadinessCtx } from '@/lib/readiness/useReadiness'
import { useReadinessStore } from '@/stores/readinessStore'
import { Icon } from '@/components/readiness/primitives'
import { Tooltip } from '@/components/readiness/Term'
import { timeAgo } from '@/lib/readiness/util'
import { useBack } from '@/lib/readiness/nav'
import { LockedItem } from '@/components/readiness/LockedItem'
import { useIsLocked } from '@/stores/profileStore'

// Page header shared by the module pages (§6.1): title, subtitle, sync status
// + actions on the right. Wraps content in `.rd` so the tokens apply. Detail
// pages pass `back`: the origin-aware back link sits directly below the module
// tab strip and above the title (I5 §4.2: 16px below the strip, 12px above).
export function RdPage({ title, subtitle, right, back, children, testId }: { title: string; subtitle?: ReactNode; right?: ReactNode; back?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <div className="rd rd-page -m-6 min-h-[calc(100vh-109px)] p-4 sm:p-6" data-testid={testId}>
      {back && <div className="-mt-2 mb-3" data-testid="back-link">{back}</div>}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="rd-page-title">{title}</h1>
          {subtitle && <p className="mt-1 text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>{subtitle}</p>}
        </div>
        {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
      </div>
      {children}
    </div>
  )
}

// Back link (I5 §4): label and target follow the page's origin; going back
// restores the origin's scroll position and view state. Styling is the IconLink.
export function BackLink({ label, onClick }: { label?: string; onClick?: () => void }) {
  const { origin, back } = useBack()
  return (
    <button type="button" className="rd-link inline-flex items-center gap-1 text-[14px]" onClick={() => { onClick?.(); back() }} data-testid="origin-back">
      <Icon name="arrow-left" size={14} /> {label ?? origin.label}
    </button>
  )
}

// Strip sync status (I3 §1.2 as amended by I5 §16.2): `no changes` / `up to
// date` when a run wrote nothing; `{n} changes` as a link to the summary after
// an automatic run with changes, until viewed or the next run. A failed run
// is retried from the user menu's `Sync now`.
export function SyncStatus({ ctx, onOpenSummary }: { ctx: ReadinessCtx; onOpenSummary?: (runId: string) => void }) {
  const { lastSuccess, lastRun, syncing } = ctx
  const viewed = useReadinessStore((s) => s.syncViewed)
  if (syncing) {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
        <Icon name="sync" size={13} className="animate-spin" /> Syncing…
      </span>
    )
  }
  if (lastRun?.status === 'failed') {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px]" style={{ color: 'var(--rd-status-warn)' }}>
        <Tooltip text={lastRun.error ?? 'Sync failed'}>
          <span className="flex items-center gap-1.5"><Icon name="warn" size={13} /> Sync failed {timeAgo(lastRun.started_at)}</span>
        </Tooltip>
      </span>
    )
  }
  if (!lastSuccess) {
    return <span className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Not yet synced · daily</span>
  }
  const at = lastSuccess.finished_at ?? lastSuccess.started_at
  const ago = timeAgo(at)
  const n = lastSuccess.changes?.length ?? 0
  const unseen = n > 0 && !viewed[lastSuccess.id]
  return (
    <span className="flex items-center gap-1.5 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="sync-status">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--rd-status-ok)' }} aria-hidden="true" />
      Synced {ago} ·{' '}
      {n === 0 ? (ago === 'just now' ? 'no changes' : 'up to date') : unseen ? (
        <button type="button" className="rd-link" onClick={() => onOpenSummary?.(lastSuccess.id)} data-testid="sync-changes-link">{n} {n === 1 ? 'change' : 'changes'}</button>
      ) : 'daily'}
    </span>
  )
}

// Page-header actions (I3 §1.2): those of the current page only. `Sync now`
// lives in the module tab strip; CSA Submission is reached from the strip. The
// Dashboard's export is the live Summary report (I5 §5); it never depends on a
// saved assessment. In POC `Save assessment` is a locked button (I6 POC §6.4);
// a locked page passes `exportDisabled` so its export reads disabled (§5.2).
// `saveServed` is set when the page shows the readiness service's open
// assessment: saving goes there, so the POC lock on browser saves does not apply.
export function HeaderActions({ ctx, onSaveSnapshot, exportTo, exportLabel = 'Export summary report', onExport, exportDisabled, saveServed }: { ctx: ReadinessCtx; onSaveSnapshot: () => void; exportTo?: string | null; exportLabel?: string; onExport?: () => void; exportDisabled?: boolean; saveServed?: boolean }) {
  const q = `?index=${ctx.indexId}`
  const to = exportTo !== undefined ? exportTo : `/readiness/report/live${q}`
  const saveLocked = useIsLocked('save_assessment') && !saveServed
  return (
    <>
      {exportDisabled ? (
        <button type="button" className="rd-btn" disabled style={{ opacity: 0.4 }} data-testid="export-report"><Icon name="download" size={14} /> {exportLabel}</button>
      ) : to ? (
        <Link to={to} className="rd-btn" data-testid="export-report" onClick={onExport}><Icon name="download" size={14} /> {exportLabel}</Link>
      ) : (
        <Tooltip text="Save an assessment first: the report is rendered from a saved assessment.">
          <span className="rd-btn opacity-60" aria-disabled="true" data-testid="export-report"><Icon name="download" size={14} /> {exportLabel}</span>
        </Tooltip>
      )}
      {saveLocked ? (
        <LockedItem feature="save_assessment" variant="button" className="rd-btn rd-btn-primary" testId="save-assessment">Save assessment</LockedItem>
      ) : (
        <button type="button" className="rd-btn rd-btn-primary" onClick={onSaveSnapshot} data-testid="save-assessment">
          <Icon name="save" size={14} /> Save assessment
        </button>
      )}
    </>
  )
}

// Quarterly reminder (I2 §1.2, §7.2): a notification to save an assessment,
// never an automatic save and never a gate on level changes.
// Not shown while saving is locked (POC §6.4): its only action is the save.
export function ReminderBanner({ ctx, onReview }: { ctx: ReadinessCtx; onReview: () => void }) {
  const saveLocked = useIsLocked('save_assessment')
  if (!ctx.reminderDue || saveLocked) return null
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] px-4 py-2.5 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-text-secondary)' }} role="status" data-testid="reminder-banner">
      <Icon name="clock" size={14} style={{ color: 'var(--rd-status-warn)' }} />
      <span>Reminder · it is time to save an assessment ({ctx.scope.settings.snapshot_rule === 'quarterly' ? '15th of each quarter-end month' : '15th of each month'}). Live levels keep updating either way.</span>
      <button type="button" className="rd-link" onClick={onReview}>Review & save →</button>
      <button type="button" className="rd-link ml-auto" onClick={ctx.dismissReminder}>Dismiss</button>
    </div>
  )
}

export function InlineError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-[10px] px-3 py-2 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }}>
      <Icon name="warn" size={14} /> {message}
      {onRetry && <button type="button" className="rd-link ml-auto" onClick={onRetry}>Retry</button>}
    </div>
  )
}
